import React from "react";
import { X } from "lucide-react";
import { ORG_EMAIL_BOOKINGS, ORG_PHONE_RAW, ORG_FULL_NAME } from "../constants";

const PDPAModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="pdpa-modal">
      <div className="brutal-card max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl font-syne font-bold">PRIVACY POLICY & PDPA</h2>
          <button
            onClick={onClose}
            className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            data-testid="close-pdpa-btn"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h3 className="text-xl font-bold mb-3">1. Introduction</h3>
            <p>
              YCK Court RN Karaoke (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) is committed to protecting your personal data 
              in accordance with Singapore&apos;s Personal Data Protection Act 2012 (&quot;PDPA&quot;).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">2. Personal Data We Collect</h3>
            <p className="mb-2">We collect the following personal data when you register as a member:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Telephone Number</li>
              <li>Mailing Address</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">3. How We Use Your Data</h3>
            <p className="mb-2">Your personal data is used for:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Processing and managing your karaoke room bookings</li>
              <li>Sending booking confirmations and payment reminders via email</li>
              <li>Communicating with you regarding your bookings</li>
              <li>Maintaining member records for administrative purposes</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">4. Data Sharing</h3>
            <p>
              We do not sell, trade, or share your personal data with third parties except:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>When required by law or government authorities</li>
              <li>With service providers who assist in our operations (e.g., email services)</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">5. Data Security</h3>
            <div className="space-y-2">
              <p>
                We implement appropriate security measures to protect your personal data against 
                unauthorized access, alteration, disclosure, or destruction. These include:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Encryption of passwords using industry-standard bcrypt hashing</li>
                <li>Role-based access controls (Admin/KJ/Member separation)</li>
                <li>Automatic session timeout after 30 minutes of inactivity</li>
                <li>JWT token-based authentication with short-lived tokens</li>
                <li><strong>Name masking on public calendars</strong> — your full name is never displayed publicly. Only a masked version (e.g., &quot;Jo** T**&quot;) is shown on the booking calendar.</li>
                <li>Personal contact details (email, phone, address) are never exposed on public-facing pages</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">6. Data Retention</h3>
            <div className="space-y-2">
              <p>We retain your personal data only for as long as necessary. Specific retention periods:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Active bookings:</strong> Retained while the booking is active or pending.</li>
                <li><strong>Completed bookings:</strong> 3 years from completion date (for financial audit and tax compliance).</li>
                <li><strong>Cancelled bookings:</strong> 1 year, after which personal data is anonymized (booking records retained for reporting).</li>
                <li><strong>Member accounts:</strong> Until the account holder requests deletion, or 2 years of inactivity.</li>
                <li><strong>Password reset tokens:</strong> Automatically deleted after 24 hours.</li>
                <li><strong>Audit logs:</strong> 3 years for operational and compliance purposes.</li>
              </ul>
              <p className="text-sm text-gray-600 mt-2">
                After the retention period, personal data is either permanently deleted or anonymized such 
                that it can no longer be linked to any individual.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">7. Your Rights</h3>
            <p className="mb-2">Under the Personal Data Protection Act (PDPA), you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Access</strong> — Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction</strong> — Request correction of inaccurate or incomplete data.</li>
              <li><strong>Withdrawal of Consent</strong> — Withdraw your consent for data processing at any time.</li>
              <li><strong>Deletion</strong> — Request deletion of your personal data (subject to legal retention requirements).</li>
            </ul>
            <div className="bg-blue-50 border-2 border-blue-300 p-3 mt-3 rounded">
              <p className="font-bold text-blue-800 text-sm mb-1">How to exercise your rights:</p>
              <p className="text-sm">
                Email our Data Protection Officer at <strong>{ORG_EMAIL_BOOKINGS}</strong> with the subject 
                line &quot;PDPA Request&quot;. Include your full name and the specific request. We will respond 
                within 30 business days as required by PDPA.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">8. Booking & Payment Policy</h3>
            <div className="space-y-2">
              <p className="font-bold text-red-700">Strictly No Refunds:</p>
              <p>
                All confirmed bookings are non-refundable. In exceptional circumstances, the Admin 
                may offer a one-time date change on a case-by-case basis. This is not guaranteed.
              </p>
              <p className="font-bold mt-3">Payment:</p>
              <p>
                Please ensure prompt payment after receiving the onePA payment link from Admin. 
                A reminder will be sent 7 days before your booking.
              </p>
              <p className="font-bold mt-3">Advance Booking:</p>
              <p>
                No new bookings or modifications can be made within 2 hours of a session&apos;s start time.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">9. Booking Visibility</h3>
            <p>
              For transparency, booked time slots are displayed on the public calendar. However, 
              only a <strong>masked version of your name</strong> is shown (e.g., &quot;Jo** T**&quot;). Your full 
              name, email, phone number, and address are <strong>never visible</strong> to other users or 
              the public. Only authorized Admins can access full member details for operational purposes.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">14. Contact Information</h3>
            <p>
              For any questions or requests regarding your personal data, please contact us at:
            </p>
            <p className="mt-2 font-bold">
              Email: {ORG_EMAIL_BOOKINGS}<br />
              Phone: {ORG_PHONE_RAW}
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">10. Data Breach Notification</h3>
            <p>
              In the event of a data breach that is likely to result in significant harm to affected 
              individuals, we will:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Notify the Personal Data Protection Commission (PDPC) within 3 calendar days of becoming aware of the breach.</li>
              <li>Notify affected individuals as soon as practicable if the breach is likely to result in significant harm.</li>
              <li>Take immediate steps to contain and remediate the breach.</li>
              <li>Maintain a breach register documenting all incidents and actions taken.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">11. Cross-border Data Transfer</h3>
            <p>
              We use third-party service providers (such as email delivery services) that may process 
              your data outside of Singapore. Where your data is transferred overseas, we ensure that 
              the recipient provides a comparable standard of data protection in accordance with the 
              PDPA. Data transferred is limited to what is necessary for operational purposes (e.g., 
              sending booking confirmations and payment receipts via email).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">12. Data Protection Officer</h3>
            <p>
              We have designated a Data Protection Officer (DPO) who is responsible for ensuring 
              compliance with the PDPA. For any data protection enquiries, access requests, or 
              complaints, please contact:
            </p>
            <div className="bg-gray-50 border-2 border-gray-300 p-3 mt-2 rounded">
              <p className="font-bold">Data Protection Officer</p>
              <p className="text-sm mt-1">
                {ORG_FULL_NAME}<br />
                Email: <strong>{ORG_EMAIL_BOOKINGS}</strong><br />
                Phone: <strong>{ORG_PHONE_RAW}</strong>
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold mb-3">13. Updates to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on our 
              website, and we will notify you via email if there are significant changes.
            </p>
          </section>

          <section className="bg-accent border-2 border-black p-4">
            <p className="font-bold">
              Last Updated: July 2026
            </p>
          </section>
        </div>

        <button
          onClick={onClose}
          className="brutal-btn-primary w-full mt-6"
          data-testid="close-pdpa-bottom-btn"
        >
          CLOSE
        </button>
      </div>
    </div>
  );
};

export default PDPAModal;
